import { getDriveEventsOptions, getDrivenStreetSegmentsOptions, getVehicleLastStatusOptions } from '@/api/main'
import {
  ActionIcon,
  Chip,
  Group,
  Loader,
  Slider,
  Stack,
  Switch,
  Text,
  useMantineTheme,
  useMantineColorScheme,
  MantineTheme,
} from '@mantine/core'
import { useQuery, useSuspenseQueries } from '@tanstack/react-query'
import { milliseconds } from 'date-fns'
import dayjs from 'dayjs'
import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { last } from 'lodash-es'
import Map, { Source, Layer, Marker, Popup, MapRef, MapMouseEvent } from 'react-map-gl/mapbox'
import type { FeatureCollection, LineString, Point } from 'geojson'
import 'mapbox-gl/dist/mapbox-gl.css'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import type { DriveDetectionModel, DriveSignPostModel, DrivenStreetSegmentModel } from '@/api/main/types.gen'
import type { GeographicCoordinates } from '@/api/main/types.gen'
import styles from './DriveDetailsMap.module.css'
import { MAPBOX_PUBLIC_TOKEN } from '@/constants'
import { useTranslation } from 'react-i18next'
import CopyableValue from '@/components/Common/CopyableValue'
import { getTimezone } from '@/utils/datetime'
import {
  detectionsToGeoJSON,
  drivenSegmentsToGeoJSON,
  explodeOffsets,
  segmentMidpoint,
  signPostStacksToGeoJSON,
  stackSignPosts,
} from './driveDetailsMapData'

const DETECTIONS_LAYER = 'detection-lines'
const SIGN_POSTS_LAYER = 'sign-post-circles'
const EXPLODED_SIGN_POSTS_LAYER = 'exploded-sign-post-circles'

type EventVisibility = {
  vehicles: boolean
  signPosts: boolean
  segments: boolean
}

type PopupContent =
  | { kind: 'detections'; detections: DriveDetectionModel[] }
  | { kind: 'signPost'; signPost: DriveSignPostModel }
  | { kind: 'signPostStack'; count: number }

type PopupInfo = {
  longitude: number
  latitude: number
  content: PopupContent
  // Pinned popups stay open (and ignore hovering) so that their values can be clicked and copied
  pinned: boolean
}

const formatObservedAt = (observedAt: string) => dayjs.utc(observedAt).tz(getTimezone()).format('DD.MM.YYYY HH:mm:ss')

const VehiclePositionMarker = ({
  position,
  azimuth,
  theme,
}: {
  position: GeographicCoordinates
  azimuth?: number
  theme: MantineTheme
}) => {
  const centerX = 24
  const centerY = 24
  const arrowPoints = [
    [centerX, centerY],
    [centerX + 16, centerY + 14],
    [centerX, centerY - 18],
    [centerX - 16, centerY + 14],
    [centerX, centerY],
  ]
    .map(it => it.join(','))
    .join(' ')

  return (
    <Marker longitude={position.longitude} latitude={position.latitude} anchor="center">
      <svg width={48} height={48} style={{ overflow: 'visible' }}>
        <circle cx={centerX} cy={centerY} r={24} fill={theme.colors.red[6]} />
        <polygon
          points={arrowPoints}
          fill={theme.colors.gray[0]}
          transform={`rotate(${azimuth ?? 0} ${centerX} ${centerY})`}
        />
      </svg>
    </Marker>
  )
}

// Czech License Plate styled component
const CzechLicensePlate = ({ plate }: { plate: string }) => {
  return (
    <div className={styles.czechLicensePlate}>
      <div className={styles.czechPlateBlueStrip}></div>
      <div className={styles.czechPlateText}>{plate}</div>
    </div>
  )
}

const LabeledValue = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    {children}
  </div>
)

const EventPopupContent = ({ content, pinned }: { content: PopupContent; pinned: boolean }) => {
  const { t } = useTranslation('driveDetails')

  if (content.kind === 'signPostStack') {
    return (
      <Stack gap={2}>
        <Text size="sm" fw={600}>
          {t('signPostDetectionCount', { count: content.count })}
        </Text>
        <Text size="xs" c="dimmed">
          {t('clickToExpand')}
        </Text>
      </Stack>
    )
  }

  return (
    <Stack gap="sm">
      {content.kind === 'detections' ? (
        content.detections.map(detection => (
          <Stack key={detection.cis_event_id} gap={4}>
            {detection.plate && <CzechLicensePlate plate={detection.plate} />}
            <LabeledValue label={t('observedAt')}>
              <Text size="sm">{formatObservedAt(detection.observed_at)}</Text>
            </LabeledValue>
            <LabeledValue label={t('detectionId')}>
              <CopyableValue value={detection.cis_event_id} />
            </LabeledValue>
          </Stack>
        ))
      ) : (
        <Stack gap={4}>
          <Text size="sm" fw={600}>
            {t('signPostDetection')}
          </Text>
          <LabeledValue label={t('observedAt')}>
            <Text size="sm">{formatObservedAt(content.signPost.observed_at)}</Text>
          </LabeledValue>
          <LabeledValue label={t('detectionId')}>
            <CopyableValue value={content.signPost.cis_event_id} />
          </LabeledValue>
          <LabeledValue label={t('signPostId')}>
            <CopyableValue value={content.signPost.sign_post_id} />
          </LabeledValue>
        </Stack>
      )}
      {!pinned && (
        <Text size="xs" c="dimmed">
          {t('clickToPin')}
        </Text>
      )}
    </Stack>
  )
}

const DrivenSegmentsSlider = ({
  segments,
  isLoading,
  highlightedSegment,
  onHighlightedSegmentChange,
}: {
  segments: DrivenStreetSegmentModel[] | undefined
  isLoading: boolean
  highlightedSegment: number
  onHighlightedSegmentChange: (index: number) => void
}) => {
  const { t } = useTranslation('driveDetails')

  if (isLoading || !segments) {
    return <Loader size="sm" mb="md" />
  }

  if (segments.length === 0) {
    return (
      <Text size="sm" c="dimmed" mb="md">
        {t('noDrivenSegments')}
      </Text>
    )
  }

  const segmentLabel = (index: number) => {
    const segment = segments[index]
    return `${index + 1}: ${segment.street_name ?? segment.street_segment_id} (${formatObservedAt(segment.observed_at)})`
  }
  const current = segments[highlightedSegment]

  return (
    <Stack gap="xs" mb="md">
      <Group justify="space-between">
        <Slider
          w="calc(100% - 80px)"
          value={highlightedSegment}
          onChange={onHighlightedSegmentChange}
          min={0}
          max={segments.length - 1}
          label={segmentLabel}
          styles={{ track: { backgroundColor: 'var(--mantine-color-gray-6)' } }}
        />
        <Group gap={8}>
          <ActionIcon
            onClick={() => onHighlightedSegmentChange(highlightedSegment - 1)}
            disabled={highlightedSegment <= 0}
          >
            <FaChevronLeft />
          </ActionIcon>
          <ActionIcon
            onClick={() => onHighlightedSegmentChange(highlightedSegment + 1)}
            disabled={highlightedSegment + 1 >= segments.length}
          >
            <FaChevronRight />
          </ActionIcon>
        </Group>
      </Group>
      {current && (
        <Group gap="xs">
          <Text size="sm">{segmentLabel(highlightedSegment)}</Text>
          {current.is_reverse_direction && (
            <Text size="sm" c="dimmed">
              · {t('reverseDirection')}
            </Text>
          )}
          <Text size="sm" c="dimmed">
            · {t('coveredRatio', { percent: Math.round(current.covered_ratio * 100) })}
          </Text>
          <CopyableValue value={current.street_segment_id} />
        </Group>
      )}
    </Stack>
  )
}

const DriveDetailsMapGL = ({
  vehicleId,
  showPatrolVehiclePosition,
  driveId,
  isInactive,
}: {
  vehicleId: string
  showPatrolVehiclePosition: boolean
  driveId: string
  isInactive?: boolean
}) => {
  const { t } = useTranslation('driveDetails')
  const theme = useMantineTheme()
  const { colorScheme } = useMantineColorScheme()
  const [zoom, setZoom] = useState(18)
  const [userDefinedMapCenter, setUserDefinedMapCenter] = useState<GeographicCoordinates | null>(null)
  const [autoUpdateMapPosition, setAutoUpdateMapPosition] = useState(!isInactive)
  const mapRef = useRef<MapRef>(null)

  const [visibility, setVisibility] = useState<EventVisibility>({ vehicles: true, signPosts: true, segments: false })
  const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null)
  const [explodedStackKey, setExplodedStackKey] = useState<string | null>(null)
  const [highlightedSegment, setHighlightedSegment] = useState(0)

  const [
    { data: driveEvents },
    {
      data: { status: lastStatus },
    },
  ] = useSuspenseQueries({
    queries: [
      {
        ...getDriveEventsOptions({ path: { drive_id: Number(driveId) } }),
        refetchInterval: milliseconds({ seconds: 1 }),
      },
      {
        ...getVehicleLastStatusOptions({ path: { vehicle_id: vehicleId } }),
        refetchInterval: milliseconds({ seconds: 1 }),
      },
    ],
  })

  // Segment geometries are heavy, so they are only fetched when the segments are displayed
  const { data: drivenSegmentsData, isLoading: isLoadingDrivenSegments } = useQuery({
    ...getDrivenStreetSegmentsOptions({ path: { drive_id: Number(driveId) } }),
    enabled: visibility.segments,
    refetchInterval: isInactive ? false : milliseconds({ seconds: 10 }),
  })
  const drivenSegments = drivenSegmentsData?.segments

  // Keep the slider within range when the list of segments changes
  const highlightedSegmentIndex = drivenSegments?.length ? Math.min(highlightedSegment, drivenSegments.length - 1) : 0

  // Calculate auto-update center
  const autoUpdateMapCenter = useMemo(() => {
    if (showPatrolVehiclePosition && lastStatus) {
      return lastStatus.position
    }
    if (driveEvents.detections.length > 0) {
      return last(driveEvents.detections)!.rear_center
    }
    if (driveEvents.sign_posts.length > 0) {
      return last(driveEvents.sign_posts)!.position
    }
    return null
  }, [showPatrolVehiclePosition, lastStatus, driveEvents.detections, driveEvents.sign_posts])

  const mapCenter = userDefinedMapCenter ?? autoUpdateMapCenter

  const detectionsGeoJSON = useMemo(() => detectionsToGeoJSON(driveEvents.detections), [driveEvents.detections])

  const signPostStacks = useMemo(() => stackSignPosts(driveEvents.sign_posts), [driveEvents.sign_posts])
  const signPostStacksByKey = useMemo(
    () => Object.fromEntries(signPostStacks.map(stack => [stack.key, stack])),
    [signPostStacks]
  )
  const signPostsGeoJSON = useMemo(() => signPostStacksToGeoJSON(signPostStacks), [signPostStacks])

  const drivenSegmentsGeoJSON = useMemo(
    () => drivenSegmentsToGeoJSON(drivenSegments ?? [], highlightedSegmentIndex),
    [drivenSegments, highlightedSegmentIndex]
  )

  // View state for Mapbox
  const [viewState, setViewState] = useState(() => ({
    longitude: mapCenter?.longitude ?? 14.41204,
    latitude: mapCenter?.latitude ?? 50.01079,
    zoom: zoom,
  }))

  // Update view state when center or zoom changes
  useEffect(() => {
    if (mapCenter) {
      if (autoUpdateMapPosition && !isInactive) {
        setViewState(prev => ({
          ...prev,
          longitude: mapCenter.longitude,
          latitude: mapCenter.latitude,
        }))
      }
    }
  }, [mapCenter, autoUpdateMapPosition, isInactive])

  useEffect(() => {
    setViewState(prev => ({
      ...prev,
      zoom: zoom,
    }))
  }, [zoom])

  // Sign post detections at exactly the same position would be impossible to tell apart, so the
  // selected stack gets spread around its position in screen space. Recomputed on every view change.
  const explodedStack = explodedStackKey ? signPostStacksByKey[explodedStackKey] : undefined
  const explodedSignPostsGeoJSON = useMemo<FeatureCollection<Point | LineString>>(() => {
    const map = mapRef.current
    if (!explodedStack || !map) return { type: 'FeatureCollection', features: [] }

    const center = map.project([explodedStack.longitude, explodedStack.latitude])
    const centerCoordinates: [number, number] = [explodedStack.longitude, explodedStack.latitude]
    const offsets = explodeOffsets(explodedStack.signPosts.length)

    return {
      type: 'FeatureCollection',
      features: explodedStack.signPosts.flatMap((_, index) => {
        const [dx, dy] = offsets[index]
        const { lng, lat } = map.unproject([center.x + dx, center.y + dy])
        return [
          {
            type: 'Feature' as const,
            geometry: { type: 'LineString' as const, coordinates: [centerCoordinates, [lng, lat]] },
            properties: {},
          },
          {
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [lng, lat] },
            properties: { index },
          },
        ]
      }),
    }
    // viewState is a dependency because the screen-space offsets change with every pan/zoom
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explodedStack, viewState])

  const interactiveLayerIds = useMemo(
    () => [
      ...(visibility.vehicles ? [DETECTIONS_LAYER] : []),
      ...(visibility.signPosts ? [SIGN_POSTS_LAYER, EXPLODED_SIGN_POSTS_LAYER] : []),
    ],
    [visibility.vehicles, visibility.signPosts]
  )

  const popupContentAt = useCallback(
    (event: MapMouseEvent): PopupInfo | null => {
      const features = event.features ?? []
      const { lng: longitude, lat: latitude } = event.lngLat

      // Sign posts are small points drawn above the detections, so they take precedence
      const explodedFeature = features.find(f => f.layer?.id === EXPLODED_SIGN_POSTS_LAYER)
      if (explodedFeature && explodedStack) {
        const signPost = explodedStack.signPosts[explodedFeature.properties?.index]
        if (signPost) {
          const [lng, lat] = (explodedFeature.geometry as Point).coordinates
          return { longitude: lng, latitude: lat, content: { kind: 'signPost', signPost }, pinned: false }
        }
      }

      const stackFeature = features.find(f => f.layer?.id === SIGN_POSTS_LAYER)
      const stack = stackFeature ? signPostStacksByKey[stackFeature.properties?.key] : undefined
      if (stack && stack.key !== explodedStackKey) {
        const position = { longitude: stack.longitude, latitude: stack.latitude }
        if (stack.signPosts.length === 1) {
          return { ...position, content: { kind: 'signPost', signPost: stack.signPosts[0] }, pinned: false }
        }
        return { ...position, content: { kind: 'signPostStack', count: stack.signPosts.length }, pinned: false }
      }

      // Overlapping detections, most recent first
      const detections = features
        .filter(f => f.layer?.id === DETECTIONS_LAYER)
        .map(f => f.properties?.index as number)
        .sort((a, b) => b - a)
        .map(index => driveEvents.detections[index])
        .filter(Boolean)
      if (detections.length > 0) {
        return { longitude, latitude, content: { kind: 'detections', detections }, pinned: false }
      }

      return null
    },
    [driveEvents.detections, explodedStack, explodedStackKey, signPostStacksByKey]
  )

  const onHover = useCallback(
    (event: MapMouseEvent) => {
      if (popupInfo?.pinned) return
      setPopupInfo(popupContentAt(event))
    },
    [popupContentAt, popupInfo?.pinned]
  )

  const onClick = useCallback(
    (event: MapMouseEvent) => {
      const info = popupContentAt(event)

      if (info?.content.kind === 'signPostStack') {
        const stackFeature = event.features?.find(f => f.layer?.id === SIGN_POSTS_LAYER)
        setExplodedStackKey(stackFeature?.properties?.key ?? null)
        setPopupInfo(null)
        return
      }

      if (info) {
        setPopupInfo({ ...info, pinned: true })
        return
      }

      // Click into empty space closes everything
      setPopupInfo(null)
      setExplodedStackKey(null)
    },
    [popupContentAt]
  )

  const toggleVisibility = (key: keyof EventVisibility, checked: boolean) => {
    setVisibility(prev => ({ ...prev, [key]: checked }))
    setPopupInfo(null)
    if (key === 'signPosts') setExplodedStackKey(null)
  }

  const onHighlightedSegmentChange = (index: number) => {
    setHighlightedSegment(index)
    const midpoint = drivenSegments?.[index] ? segmentMidpoint(drivenSegments[index]) : null
    if (midpoint) {
      setAutoUpdateMapPosition(false)
      setUserDefinedMapCenter(midpoint)
      setViewState(prev => ({ ...prev, ...midpoint }))
    }
  }

  if (
    driveEvents.detections.length === 0 &&
    driveEvents.sign_posts.length === 0 &&
    driveEvents.segments.length === 0 &&
    (!showPatrolVehiclePosition || !lastStatus)
  ) {
    return null
  }

  return (
    <>
      <Group justify="space-between" mb="md">
        <Switch
          checked={autoUpdateMapPosition}
          disabled={isInactive}
          onChange={event => {
            setAutoUpdateMapPosition(event.currentTarget.checked)
            setUserDefinedMapCenter(event.currentTarget.checked ? null : autoUpdateMapCenter)
          }}
          label={t('followPatrolVehicle')}
        />
        <Group gap="xs">
          <Chip checked={visibility.vehicles} onChange={checked => toggleVisibility('vehicles', checked)}>
            {t('detectedVehicles')} ({driveEvents.detections.length})
          </Chip>
          <Chip checked={visibility.signPosts} onChange={checked => toggleVisibility('signPosts', checked)}>
            {t('detectedSignPosts')} ({driveEvents.sign_posts.length})
          </Chip>
          <Chip checked={visibility.segments} onChange={checked => toggleVisibility('segments', checked)}>
            {t('drivenRouteSegments')} ({driveEvents.segments.length})
          </Chip>
        </Group>
      </Group>
      <div style={{ position: 'relative' }}>
        <Map
          ref={mapRef}
          id="driveDetailsMap"
          {...viewState}
          onMove={evt => {
            setViewState(evt.viewState)
            setZoom(evt.viewState.zoom)
            if (!autoUpdateMapPosition) {
              setUserDefinedMapCenter({
                latitude: evt.viewState.latitude,
                longitude: evt.viewState.longitude,
              })
            }
          }}
          onMouseMove={onHover}
          onMouseOut={() => {
            if (!popupInfo?.pinned) setPopupInfo(null)
          }}
          onClick={onClick}
          interactiveLayerIds={interactiveLayerIds}
          cursor={popupInfo && !popupInfo.pinned ? 'pointer' : undefined}
          dragPan={isInactive || !autoUpdateMapPosition}
          style={{
            width: '100%',
            height: '50em',
            borderRadius: 'var(--mantine-radius-md)',
            marginBottom: 'var(--mantine-spacing-md)',
          }}
          mapStyle={`mapbox://styles/mapbox/${colorScheme === 'light' || colorScheme === 'auto' ? 'streets-v12' : 'dark-v11'}`}
          mapboxAccessToken={MAPBOX_PUBLIC_TOKEN}
        >
          {/* Driven street segments */}
          <Source id="drivenSegments" type="geojson" data={drivenSegmentsGeoJSON}>
            <Layer
              id="driven-segment-lines"
              type="line"
              layout={{ visibility: visibility.segments ? 'visible' : 'none', 'line-cap': 'round' }}
              paint={{
                'line-color': ['case', ['get', 'highlighted'], theme.colors.violet[5], theme.colors.teal[5]],
                'line-width': ['case', ['get', 'highlighted'], 8, 5],
                'line-opacity': ['case', ['get', 'highlighted'], 1, 0.6],
              }}
            />
          </Source>

          {/* Vehicle detection lines */}
          <Source id="detections" type="geojson" data={detectionsGeoJSON}>
            <Layer
              id={DETECTIONS_LAYER}
              type="line"
              layout={{ visibility: visibility.vehicles ? 'visible' : 'none' }}
              paint={{
                'line-color': theme.colors.blue[9],
                'line-width': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  10, // At zoom level 10
                  3, // Line width is 3px
                  12, // At zoom level 12
                  3, // Line width is 3px
                  14, // At zoom level 14
                  3.5, // Line width is 3.5px
                  16, // At zoom level 16
                  4, // Line width is 4px
                  18, // At zoom level 18
                  5, // Line width is 5px
                  20, // At zoom level 20
                  6.5, // Line width is 6.5px
                  22, // At zoom level 22
                  8, // Line width is 8px
                  24, // At zoom level 24
                  10, // Line width is 10px
                ],
              }}
            />
          </Source>

          {/* Sign posts; stacks of repeated detections are drawn bigger with a count */}
          <Source id="signPosts" type="geojson" data={signPostsGeoJSON}>
            <Layer
              id={SIGN_POSTS_LAYER}
              type="circle"
              layout={{ visibility: visibility.signPosts ? 'visible' : 'none' }}
              paint={{
                'circle-radius': ['case', ['>', ['get', 'count'], 1], 9, 5],
                'circle-color': 'white',
                'circle-stroke-width': ['case', ['>', ['get', 'count'], 1], 2, 1],
                'circle-stroke-color': theme.colors.blue[9],
                'circle-opacity': ['case', ['==', ['get', 'key'], explodedStackKey ?? ''], 0.4, 1],
                'circle-stroke-opacity': ['case', ['==', ['get', 'key'], explodedStackKey ?? ''], 0.4, 1],
              }}
            />
            <Layer
              id="sign-post-counts"
              type="symbol"
              filter={['all', ['>', ['get', 'count'], 1], ['!=', ['get', 'key'], explodedStackKey ?? '']]}
              layout={{
                visibility: visibility.signPosts ? 'visible' : 'none',
                'text-field': ['to-string', ['get', 'count']],
                'text-size': 11,
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }}
              paint={{ 'text-color': theme.colors.blue[9] }}
            />
          </Source>

          {/* Exploded stack of sign post detections */}
          <Source id="explodedSignPosts" type="geojson" data={explodedSignPostsGeoJSON}>
            <Layer
              id="exploded-sign-post-legs"
              type="line"
              filter={['==', ['geometry-type'], 'LineString']}
              layout={{ visibility: visibility.signPosts ? 'visible' : 'none' }}
              paint={{ 'line-color': theme.colors.blue[9], 'line-width': 1 }}
            />
            <Layer
              id={EXPLODED_SIGN_POSTS_LAYER}
              type="circle"
              filter={['==', ['geometry-type'], 'Point']}
              layout={{ visibility: visibility.signPosts ? 'visible' : 'none' }}
              paint={{
                'circle-radius': 6,
                'circle-color': 'white',
                'circle-stroke-width': 2,
                'circle-stroke-color': theme.colors.blue[9],
              }}
            />
          </Source>

          {/* Vehicle position marker */}
          {lastStatus && showPatrolVehiclePosition && (
            <VehiclePositionMarker position={lastStatus.position} azimuth={lastStatus.azimuth} theme={theme} />
          )}

          {/* Event details */}
          {popupInfo && (
            <Popup
              // Popup options (e.g. the close button) are only applied on creation
              key={popupInfo.pinned ? 'pinned' : 'hover'}
              longitude={popupInfo.longitude}
              latitude={popupInfo.latitude}
              closeButton={popupInfo.pinned}
              closeOnClick={false}
              onClose={() => setPopupInfo(null)}
              anchor="top"
              offset={8}
              maxWidth="none"
              className={styles.eventPopup}
            >
              <EventPopupContent content={popupInfo.content} pinned={popupInfo.pinned} />
            </Popup>
          )}
        </Map>
      </div>
      {visibility.segments && (
        <DrivenSegmentsSlider
          segments={drivenSegments}
          isLoading={isLoadingDrivenSegments}
          highlightedSegment={highlightedSegmentIndex}
          onHighlightedSegmentChange={onHighlightedSegmentChange}
        />
      )}
    </>
  )
}

export default DriveDetailsMapGL
